import { useEffect } from 'react';
import { useGetRegistryPurchaseConfirmationDetailsQuery } from '@graphql/generated';
import { useTranslation } from '@shared/core';
import { useQueryParamHelper } from '@shared/core/queryString';
import { withWindow } from '@shared/utils/withWindow';
import { useThanksForRSVPTelemetry } from '@apps/thanksForRsvp/ThanksForRSVP.telemetry';
import { useFeatureValue } from '@shared/core/featureFlags';
import { isRegistryGiftWrapEcardPricePayload } from '@shared/components';
import { DEFAULT_GIFT_CARD_PRICE } from '../Congratulations/components/Offer/Offer.controller';

export const useSuccessController = (
  eventHandle: string,
  fromGiftWrap: boolean,
  hasRoomBlocksBookingUrl: boolean,
  eventId: string,
  accommodationsCardVisibilityData: {
    isAccommodationsPageEnabled?: boolean;
  },
  coupleName: Maybe<string>,
  showRegistryCard: boolean,
) => {
  const { isAccommodationsPageEnabled } = accommodationsCardVisibilityData;

  const { getValueString } = useQueryParamHelper();
  const purchaseContextId = getValueString('pcId');

  const { rsvpSentPageViewed, shopAccommodationClicked, shopRegistryClicked, backToWebsiteClicked, transactionCompleted } = useThanksForRSVPTelemetry();

  const { data } = useGetRegistryPurchaseConfirmationDetailsQuery({
    batchMode: 'fast',
    variables: purchaseContextId ? { purchaseContextId } : undefined,
    skip: !purchaseContextId,
  });
  const salesOrderId = data?.registryPurchaseConfirmationDetailsByPurchaseContextId?.salesOrder?.id;

  const { payload: rsvpCongratulationsECardPriceFlagPayload } = useFeatureValue('rsvpCongratulationsECardPrice');
  const rsvpCongratulationsECardPrice = isRegistryGiftWrapEcardPricePayload(rsvpCongratulationsECardPriceFlagPayload)
    ? rsvpCongratulationsECardPriceFlagPayload.price
    : DEFAULT_GIFT_CARD_PRICE;
  const price = `$${rsvpCongratulationsECardPrice}`;

  useEffect(() => {
    rsvpSentPageViewed({
      href: window?.location?.href ?? '',
      fromGiftWrap,
      hasRoomBlocksBookingUrl,
      hasRegistry: showRegistryCard,
      hasAccommodations: isAccommodationsPageEnabled,
    });

    if (salesOrderId) {
      transactionCompleted({ salesOrderId, price });
    }
  }, [fromGiftWrap, hasRoomBlocksBookingUrl, showRegistryCard, price, rsvpSentPageViewed, salesOrderId, transactionCompleted, isAccommodationsPageEnabled]);

  const { t } = useTranslation('thanksForRSVP');
  const tCongratulationsCard = t('congratulationsCard');
  const handleAccommodation = () => {
    shopAccommodationClicked({ isGWScreen: false });
    withWindow(global => global.open(`${global.location.origin}/${eventHandle}/accommodations`, '_blank'));
  };
  const handleShopRegistry = () => {
    shopRegistryClicked({ isGWScreen: false });
    withWindow(global => {
      global.location.href = `${global.location.origin}/${eventHandle}/registry`;
    });
  };
  const handleBackToWebsite = () => {
    backToWebsiteClicked({ isGWScreen: false });
    withWindow(global => {
      global.location.href = `${global.location.origin}/${eventHandle}`;
    });
  };
  const message = `${tCongratulationsCard.yourRSVP()} ${fromGiftWrap ? tCongratulationsCard.andCardHave() : tCongratulationsCard.has()} ${tCongratulationsCard.beenSent({
    coupleName: coupleName ? ` for ${coupleName}` : '',
  })}`;

  return {
    tCongratulationsCard,
    handleAccommodation,
    handleShopRegistry,
    handleBackToWebsite,
    message,
    showRoomBlocksCard: isAccommodationsPageEnabled,
    hasRoomBlocksBookingUrl: hasRoomBlocksBookingUrl,
  };
};
